import React, { useState, useEffect } from 'react';
import { Button, Card, Col, DatePicker, Form, Row, Table } from 'antd';
const { RangePicker } = DatePicker;
import { useForm } from 'antd/lib/form/Form';
import { SaleOrderService } from '@gtpl/shared-services/sale-management';
import { AlertMessages } from '@gtpl/shared-utils/alert-messages';
import { DutyDrawBackDateReq } from '@gtpl/shared-models/finance';
import { ColumnsType } from 'antd/lib/table';

interface ExportData {
  key:string,
  particulars: string;
  fullyReceived: string;
  partiallyReceived: string;
  toBeReceived: string;
  fullyReceivedUsd: string;
  partiallyReceivedUsd: string;
  toBeReceivedUsd: string;

}

const ExportReceivablesReport: React.FC = () => {
  const [form] = useForm();
  const Service = new SaleOrderService();
  const [getAllData, setGetAllData] = useState<ExportData[]>([]);
  const [selectedFromDate, setSelectedFromDate] = useState<Date | undefined>(undefined);
  const [selectedToDate, setSelectedToDate] = useState<Date | undefined>(undefined);

  const getExportsData = async (onReset = false) => {
    let fromDate = onReset ? undefined : selectedFromDate;
    let toDate = onReset ? undefined : selectedToDate;

    const req = new DutyDrawBackDateReq(fromDate, toDate);

    try {
      const response = await Service.getReceivablesBillingData(req);
      if (response.status && response.data) {
        const { BMR_IPL, BMR_Exports } = response.data;

        const displayData: ExportData[] = [
          {
            key: "1",
            particulars: "BMR IPL",
            fullyReceived: BMR_IPL.totalFullyPaidAmount,
            partiallyReceived: BMR_IPL.totalPartiallyPaidAmount,
            toBeReceived: BMR_IPL.totalOpenAmount,
            toBeReceivedUsd: BMR_IPL.totalOpenAmountUsd,
            partiallyReceivedUsd: BMR_IPL.totalPartiallyPaidAmountUsd,
            fullyReceivedUsd: BMR_IPL.totalFullyPaidAmountUsd,


            
          },
          {
            key: "2",
            particulars: "BMR Exports",
            fullyReceived: BMR_Exports.totalFullyPaidAmount,
            partiallyReceived: BMR_Exports.totalPartiallyPaidAmount,
            toBeReceived: BMR_Exports.totalOpenAmount,
            toBeReceivedUsd: BMR_Exports.totalOpenAmountUsd,
            partiallyReceivedUsd: BMR_Exports.totalPartiallyPaidAmountUsd,
            fullyReceivedUsd: BMR_Exports.totalFullyPaidAmountUsd,

          },
        ];
        

        setGetAllData(displayData);
      } else {
        AlertMessages.getErrorMessage("No data available");
      }
    } catch (err: any) {
      AlertMessages.getErrorMessage(err.internalMessage || "Error fetching data");
    }
  };

  useEffect(() => {
    getExportsData();
  }, []);

  const onReset = () => {
    getExportsData(true);
    form.resetFields();
    setSelectedFromDate(undefined);
    setSelectedToDate(undefined);
    setGetAllData([]);
  };

  const handleDateChange = (value: any) => {
    if (value) {
      const fromDate = new Date(value[0].format('YYYY-MM-DD'));
      const toDate = new Date(value[1].format('YYYY-MM-DD'));
      setSelectedFromDate(fromDate);
      setSelectedToDate(toDate);
    }
  };

  const formatCurrency = (value: number | string) => {
    if (!value) return "₹0.00";
    const numberValue = typeof value === "string" ? parseFloat(value) : value;
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(numberValue);
  };
  

  
  const columns: ColumnsType<ExportData> = [
    {
      title: "Particulars",
      dataIndex: "particulars",
      key: "particulars",
      width: "30%",
      render: (text: string) => <strong>{text}</strong>, 
    },
    {
      title: "To Be Received(INR)",
      dataIndex: "toBeReceived",
      key: "toBeReceived",
      render: (text: string) => formatCurrency(text),
      sorter: (a: any, b: any) => parseFloat(a.toBeReceived) - parseFloat(b.toBeReceived),
      sortDirections: ["ascend", "descend"], 
    },
    {
      title: "To Be Received(USD)",
      dataIndex: "toBeReceivedUsd",
      key: "toBeReceivedUsd",
      render: (text: string) => `$${(text)}`,
      sorter: (a: any, b: any) => parseFloat(a.toBeReceivedUsd) - parseFloat(b.toBeReceivedUsd),
      sortDirections: ["ascend", "descend"], 
    },
    {
      title: "Partially Received(INR)",
      dataIndex: "partiallyReceived",
      key: "partiallyReceived",
      render: (text: string) => formatCurrency(text),
      sorter: (a: any, b: any) => parseFloat(a.partiallyReceived) - parseFloat(b.partiallyReceived),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Partially Received(USD)",
      dataIndex: "partiallyReceivedUsd",
      key: "partiallyReceivedUsd",
      render: (text: string) => `$${(text)}`,
      sorter: (a: any, b: any) => parseFloat(a.partiallyReceivedUsdUsd) - parseFloat(b.partiallyReceivedUsdUsd),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Fully Received(INR)",
      dataIndex: "fullyReceived",
      key: "fullyReceived",
      render: (text: string) => formatCurrency(text),
      sorter: (a: any, b: any) => parseFloat(a.fullyReceived) - parseFloat(b.fullyReceived),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Fully Received(USD)",
      dataIndex: "fullyReceivedUsd",
      key: "fullyReceivedUsd",
      render: (text: string) => `$${(text)}`,
      sorter: (a: any, b: any) => parseFloat(a.fullyReceivedUsd) - parseFloat(b.fullyReceivedUsd),
      sortDirections: ["ascend", "descend"],
    },
  ];
  

  return (
    <Card
      size="small"
      title={<span style={{ color: 'white' }}>EXPORT RECEIVABLES REPORT </span>}
      style={{ textAlign: 'center' }}
      headStyle={{ backgroundColor: '#69c0ff', border: 0 }}
    >
      <Form layout="vertical" form={form}>
        <Row gutter={16} align="middle">
          <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 10 }} lg={{ span: 8 }} xl={{ span: 6 }}>
            <Form.Item
              name="dateRange"
              label="Shipping Bill Date"
              initialValue={undefined}
              rules={[
                {
                  required: false,
                  message: "Select date range",
                },
              ]}
            >
              <RangePicker onChange={handleDateChange} />
            </Form.Item>
          </Col>
          <Col style={{ paddingLeft: '10px', marginTop: '7px' }}>
            <Button type="primary" onClick={() => getExportsData()}>
              Get Report
            </Button>
          </Col>
          <Col style={{ paddingLeft: '10px', marginTop: '7px' }}>
            <Button type="primary" onClick={onReset}>
              Reset
            </Button>
          </Col>
        </Row>
      </Form>
      <Table  style={{padding:'5px 20px'}} size="small" columns={columns} dataSource={getAllData} bordered pagination={false} />
    </Card>
  );
};

export default ExportReceivablesReport;
